import { supabase } from '../lib/supabase';

export interface AgentCollectedData {
  rating?: number | null;
  comments?: string | null;
  agreed_to_review?: boolean;
  review_link_sent?: boolean;
}

/**
 * One-line, human-readable outcome for an AI agent call. Returns null for
 * human calls or unknown outcomes (renders nothing).
 */
export function agentOutcomeLine(
  outcome?: string | null,
  collected?: AgentCollectedData | null,
): string | null {
  if (!outcome) return null;
  switch (outcome) {
    case 'feedback_collected':
      return `${collected?.rating ?? '?'}/5 rating captured${collected?.review_link_sent ? ' · review link sent' : ''}`;
    case 'no_rating':
      return 'Spoke to customer — no rating given';
    case 'no_engagement':
      return 'No engagement — likely voicemail';
    case 'no_answer':
      return 'No answer';
    case 'call_failed':
      return 'Call failed';
    case 'message_taken':
      return 'Message taken';
    case 'booking_requested':
      return 'Booking requested';
    case 'question_answered':
      return 'Question answered';
    default:
      return null;
  }
}

export interface CallHistoryItem {
  id: string;
  call_sid: string;
  from_number: string;
  to_number: string;
  direction: 'inbound' | 'outbound';
  status: string;
  duration_seconds: number;
  call_time: string;
  created_at: string;
  customer_id?: string;
  viewed: boolean;
  // AI agent call identification (null = human call)
  agent_id?: string | null;
  agent_session_id?: string | null;
  agent?: {
    id: string;
    name: string;
    kind: string;
  } | null;
  // Session outcome for agent calls (from agent_call_sessions)
  agent_outcome?: string | null;
  agent_collected?: AgentCollectedData | null;
  customers?: {
    id: string;
    first_name: string;
    last_name: string;
    email?: string;
  } | null;
}

export class CallHistoryService {
  
  /**
   * Get call history for a business
   */
  static async getCallHistory(businessId: string): Promise<{ data: CallHistoryItem[] | null; error: any }> {
    try {
      console.log('🔍 Fetching call history for business:', businessId);
      
      const { data, error } = await supabase
        .from('call_history')
        .select('*')
        .eq('business_id', businessId)
        .order('call_time', { ascending: false })
        .limit(50); // Get last 50 calls

      console.log('🔍 Call history query result:', { 
        found: data?.length || 0, 
        error: error ? JSON.stringify(error) : null 
      });

      if (error) {
        console.error('Error fetching call history:', error);
        return { data: null, error };
      }

      // Resolve AI agent names + session outcomes for badge display
      if (data && data.length > 0) {
        const agentIds = [...new Set(data.filter(call => call.agent_id).map(call => call.agent_id))];
        const sessionIds = data.filter(call => call.agent_session_id).map(call => call.agent_session_id);
        if (agentIds.length > 0) {
          const [agentsRes, sessionsRes] = await Promise.all([
            supabase.from('agents_v2').select('id, name, kind').in('id', agentIds),
            sessionIds.length > 0
              ? supabase.from('agent_call_sessions').select('id, outcome, collected').in('id', sessionIds)
              : Promise.resolve({ data: [], error: null }),
          ]);
          const agents = agentsRes.data || [];
          const sessions = sessionsRes.data || [];
          data.forEach(call => {
            if (call.agent_id) {
              call.agent = agents.find((a: { id: string }) => a.id === call.agent_id) || null;
            }
            if (call.agent_session_id) {
              const session = sessions.find((s: { id: string }) => s.id === call.agent_session_id);
              call.agent_outcome = session?.outcome ?? null;
              call.agent_collected = session?.collected ?? null;
            }
          });
        }
      }

      // If we have call data and some have customer_ids, fetch customer names
      if (data && data.length > 0) {
        const callsWithCustomers = data.filter(call => call.customer_id);
        
        if (callsWithCustomers.length > 0) {
          const customerIds = callsWithCustomers.map(call => call.customer_id);
          console.log('🔍 Looking up customers for IDs:', customerIds);
          
          // Fetch customer names
          const { data: customers, error: customerError } = await supabase
            .from('customers')
            .select('id, first_name, last_name, email')
            .in('id', customerIds);
            
          console.log('🔍 Customer lookup result:', { 
            found: customers?.length || 0, 
            error: customerError ? JSON.stringify(customerError) : null,
            customers: customers 
          });
            
          if (!customerError && customers) {
            // Map customer data to call records
            data.forEach(call => {
              if (call.customer_id) {
                const customer = customers.find(c => c.id === call.customer_id);
                console.log(`🔍 Mapping customer for call ${call.id}: customer_id=${call.customer_id}, found=${!!customer}`);
                if (customer) {
                  call.customers = customer;
                  console.log(`✅ Mapped customer: ${customer.name} to call ${call.id}`);
                }
              }
            });
          }
        }
      }

      console.log('✅ Call history data with customers:', data);
      return { data: data as CallHistoryItem[], error: null };
    } catch (err) {
      console.error('Error in getCallHistory:', err);
      return { data: null, error: err };
    }
  }

  /**
   * Format phone number for display
   */
  static formatPhoneNumber(phoneNumber: string): string {
    // Remove all non-digits
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX for US numbers
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const number = cleaned.substring(1);
      return `+1 (${number.substring(0, 3)}) ${number.substring(3, 6)}-${number.substring(6)}`;
    } else if (cleaned.length === 10) {
      return `+1 (${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
    }
    
    return phoneNumber; // Return as-is if not a standard US number
  }

  /**
   * Format call duration
   */
  static formatDuration(seconds: number): string {
    if (seconds === 0) return '';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes === 0) {
      return `${remainingSeconds}s`;
    } else {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
  }

  /**
   * Format call time for display
   */
  static formatCallTime(callTime: string): string {
    const date = new Date(callTime);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const callDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    // Check if call was today
    if (callDate.getTime() === today.getTime()) {
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } else {
      // Show date for older calls
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  }

  /**
   * Get call type for display - SIMPLIFIED LOGIC
   * Outbound calls always show as blue (outgoing)
   * Inbound calls show missed (red) or answered (green) based on status
   */
  static getCallType(direction: string, status: string): 'missed' | 'incoming' | 'outgoing' {
    if (direction === 'outbound') {
      // All outbound calls show as blue - no missed/answered distinction needed
      return 'outgoing';
    } else {
      // Inbound calls: show missed (red) or answered (green) based on status
      if (status === 'missed' || status === 'no-answer') {
        return 'missed';
      } else {
        return 'incoming'; // This will show as green for answered inbound calls
      }
    }
  }

  /**
   * Get display status text based on direction and status - SIMPLIFIED LOGIC
   */
  static getDisplayStatus(direction: string, status: string): string {
    if (direction === 'outbound') {
      // All outbound calls just show "Outbound" - no missed/answered distinction
      return 'Outbound';
    } else {
      // Inbound calls show specific status
      if (status === 'missed' || status === 'no-answer') {
        return 'Missed';     // They called you, you didn't pick up
      } else if (status === 'answered') {
        return 'Answered';   // They called you, you picked up
      } else {
        // For other statuses, capitalize the first letter
        return status.charAt(0).toUpperCase() + status.slice(1);
      }
    }
  }

  /**
   * Badge label for AI agent calls — which agent placed/handled the call.
   * Returns null for human calls (no badge).
   */
  static getAgentBadgeLabel(call: Pick<CallHistoryItem, 'agent_id' | 'agent'>): string | null {
    if (!call.agent_id) return null;
    const kind = call.agent?.kind;
    if (kind === 'feedback') return 'Feedback Agent';
    if (kind === 'receptionist') return 'AI Receptionist';
    return call.agent?.name || 'AI Agent';
  }

  /**
   * Get display name - customer name if available, otherwise formatted phone number
   */
  static getDisplayName(call: CallHistoryItem): string {
    if (call.customers?.first_name) {
      const fullName = `${call.customers.first_name} ${call.customers.last_name || ''}`.trim();
      return fullName;
    }
    
    // Fallback to formatted phone number
    const phoneNumber = call.direction === 'inbound' ? call.from_number : call.to_number;
    return this.formatPhoneNumber(phoneNumber);
  }

  /**
   * Get display subtitle - phone number if customer name is shown, otherwise direction
   */
  static getDisplaySubtitle(call: CallHistoryItem): string {
    if (call.customers?.first_name) {
      // If showing customer name, show the phone number as subtitle
      const phoneNumber = call.direction === 'inbound' ? call.from_number : call.to_number;
      return this.formatPhoneNumber(phoneNumber);
    }
    
    // If no customer name, show status and direction
    const displayStatus = this.getDisplayStatus(call.direction, call.status);
    const directionText = call.direction === 'inbound' ? 'Incoming' : 'Outgoing';
    return `${displayStatus} • ${directionText}`;
  }
}
